/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import { buildApiUrl, makeAuthenticatedRequest } from '../config/api';

const parseJson = async (response) => {
  if (!response) {
    throw new Error('No response from server');
  }
  const data = await response.json();
  if (!response.ok || data?.success === false) {
    throw new Error(data?.error || 'Request failed');
  }
  return data?.data || data;
};

export const fetchPublicBlogs = async (category = 'all', searchTerm = '') => {
  const params = new URLSearchParams();
  if (category && category !== 'all') params.append('category', category);
  if (searchTerm) params.append('search', searchTerm);
  
  const url = buildApiUrl(`/api/public/blogs${params.toString() ? '?' + params.toString() : ''}`);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Unable to load blogs');
  }
  const payload = await response.json();
  return payload?.data || [];
};

export const fetchPublicBlog = async (blogId) => {
  const url = buildApiUrl(`/api/public/blogs/${blogId}`);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Unable to load blog');
  }
  const payload = await response.json();
  return payload?.data || null;
};

export const fetchAdminBlogs = async () => {
  const response = await makeAuthenticatedRequest(
    buildApiUrl('/api/admin/blogs')
  );
  return parseJson(response);
};

const buildFormData = (values) => {
  const formData = new FormData();
  formData.append('title', values.title || '');
  formData.append('excerpt', values.excerpt || '');
  formData.append('author', values.author || '');
  formData.append('category', values.category || '');
  formData.append('date', values.date || '');
  formData.append('readTime', values.readTime || '5 min read');
  formData.append('isPublished', values.isPublished ? 'true' : 'false');
  if (values.tags && Array.isArray(values.tags)) {
    formData.append('tags', values.tags.join(','));
  } else if (values.tags) {
    formData.append('tags', values.tags);
  }
  if (values.imageFile instanceof File) {
    formData.append('image', values.imageFile);
  } else if (values.imageUrl) {
    formData.append('imageUrl', values.imageUrl);
  }
  return formData;
};

export const createBlog = async (values) => {
  const response = await makeAuthenticatedRequest(
    buildApiUrl('/api/admin/blogs'),
    {
      method: 'POST',
      body: buildFormData(values)
    }
  );
  return parseJson(response);
};

export const updateBlog = async (id, values) => {
  const response = await makeAuthenticatedRequest(
    buildApiUrl(`/api/admin/blogs/${id}`),
    {
      method: 'PUT',
      body: buildFormData(values)
    }
  );
  return parseJson(response);
};

export const deleteBlog = async (id) => {
  const response = await makeAuthenticatedRequest(
    buildApiUrl(`/api/admin/blogs/${id}`),
    { method: 'DELETE' }
  );
  await parseJson(response);
};

export const toggleBlogPublish = async (id) => {
  const response = await makeAuthenticatedRequest(
    buildApiUrl(`/api/admin/blogs/${id}/toggle`),
    { method: 'POST' }
  );
  return parseJson(response);
};

// Public blog engagement APIs
export const toggleBlogLike = async (blogId, userId) => {
  const response = await fetch(
    buildApiUrl(`/api/public/blogs/${blogId}/like`),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    }
  );
  if (!response.ok) {
    throw new Error('Unable to toggle like');
  }
  const payload = await response.json();
  return payload?.data || null;
};

export const getBlogLikes = async (blogId, userId = null) => {
  const params = new URLSearchParams();
  if (userId) params.append('userId', userId);
  
  const url = buildApiUrl(`/api/public/blogs/${blogId}/likes${params.toString() ? '?' + params.toString() : ''}`);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Unable to load likes');
  }
  const payload = await response.json();
  return payload?.data || { likes: 0, isLiked: false };
};

export const addBlogComment = async (blogId, userId, userName, comment) => {
  const response = await fetch(
    buildApiUrl(`/api/public/blogs/${blogId}/comments`),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, userName, comment }),
    }
  );
  if (!response.ok) {
    throw new Error('Unable to add comment');
  }
  const payload = await response.json();
  return payload?.data || null;
};

export const getBlogComments = async (blogId) => {
  const response = await fetch(
    buildApiUrl(`/api/public/blogs/${blogId}/comments`)
  );
  if (!response.ok) {
    throw new Error('Unable to load comments');
  }
  const payload = await response.json();
  return payload?.data || { comments: [], commentCount: 0 };
};

